import { useEffect, useState } from 'react';
import { esGtin, limpiarCodigo } from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { api } from '../sync/client.js';
import { nombresDe } from '../i18n/productos.js';
import { esCopiaDeTienda } from './copia-de-tienda.js';
import { enPractica } from './practica.js';
import { cortarTexto, normalizar } from './texto.js';

/**
 * Lo que los catálogos abiertos saben de un código que la tienda no tiene.
 *
 * Open Food Facts and its sister catalogues know a good share of the packaged goods on a shop's
 * shelves by barcode: what they are called, whose they are, how big, and what the front looks like.
 * Asked through the shop's server (`apps/api/src/routes/codigos.ts`), they turn an unknown scan
 * into a suggestion: the product it probably is in the shop's own catalogue, under a name of the
 * shop's, or a new product already filled in (the owner, 2026-10-02).
 *
 * Never asked from the practice shop, which talks to no server, nor from a copy of the shop. And
 * whatever comes back is somebody's entry in a public catalogue: shown with its source, and only
 * ever a starting point the owner checks.
 */

/** What the shop's server says (`DatosDeCodigo` there). */
export interface DatosAbiertos {
  codigo: string;
  encontrado: boolean;
  ocupado?: true;
  fuente?: string;
  enlace?: string;
  nombre?: string;
  nombreEn?: string;
  marca?: string;
  cantidad?: string;
  categorias?: string[];
  foto?: boolean;
}

/** Whether this device asks at all. */
export const sePuedenConsultar = (): boolean => !enPractica() && !esCopiaDeTienda();

/** The code as it is asked, or null when it is not one a catalogue could know: they only file GTINs. */
function consultable(codigo: string): string | null {
  const limpio = limpiarCodigo(codigo);
  return sePuedenConsultar() && esGtin(limpio) ? limpio : null;
}

/** A code a catalogue could know, on a device that does not ask: the practice shop, a copy. */
const noSePregunta = (codigo: string): boolean => !sePuedenConsultar() && esGtin(limpiarCodigo(codigo));

const consultas = new Map<string, Promise<DatosAbiertos | null>>();
const fotos = new Map<string, Promise<Blob | null>>();

/**
 * What the catalogues know, asked once per code for every screen that wants it: the question
 * sheet and the form it opens share the one request. Null without signal or a server, and then
 * asked again next time, like an answer that came back busy.
 */
export function consultarCodigo(codigo: string): Promise<DatosAbiertos | null> {
  const limpio = consultable(codigo);
  if (!limpio) return Promise.resolve(null);
  let consulta = consultas.get(limpio);
  if (!consulta) {
    consulta = api.codigo(limpio).catch(() => null);
    consultas.set(limpio, consulta);
    void consulta.then((d) => {
      if (!d || d.ocupado) consultas.delete(limpio);
    });
  }
  return consulta;
}

/** The catalogue's photo of the front, as bytes from the shop's server; null when there is none. */
export function fotoAbierta(codigo: string): Promise<Blob | null> {
  const limpio = consultable(codigo);
  if (!limpio) return Promise.resolve(null);
  let foto = fotos.get(limpio);
  if (!foto) {
    foto = api.fotoDeCodigo(limpio).catch(() => null);
    fotos.set(limpio, foto);
    void foto.then((b) => {
      if (!b) fotos.delete(limpio);
    });
  }
  return foto;
}

/** For the tests: nothing asked yet. */
export function olvidarConsultas(): void {
  consultas.clear();
  fotos.clear();
}

export type EstadoConsulta =
  /** Nothing to ask: not a GTIN, or no code at all. */
  | { estado: 'nada' }
  /** One a catalogue could know, on a device that never asks: the practice shop, a copy of the shop. */
  | { estado: 'apagado' }
  | { estado: 'buscando' }
  | { estado: 'listo'; datos: DatosAbiertos | null };

/** A screen's view of `consultarCodigo`. */
export function useCodigoAbierto(codigo: string | null | undefined): EstadoConsulta {
  const limpio = codigo ? consultable(codigo) : null;
  const [respuesta, setRespuesta] = useState<{ de: string; datos: DatosAbiertos | null } | null>(null);
  useEffect(() => {
    if (!limpio) return;
    let vigente = true;
    void consultarCodigo(limpio).then((datos) => {
      if (vigente) setRespuesta({ de: limpio, datos });
    });
    return () => {
      vigente = false;
    };
  }, [limpio]);
  if (!limpio) return codigo && noSePregunta(codigo) ? { estado: 'apagado' } : { estado: 'nada' };
  return respuesta?.de === limpio ? { estado: 'listo', datos: respuesta.datos } : { estado: 'buscando' };
}

// ---------------------------------------------------------------------------
// De lo que dicen a un producto de la tienda
// ---------------------------------------------------------------------------

const UNIDADES: Record<string, string> = {
  g: 'g', gr: 'g', grs: 'g', gramos: 'g', kg: 'kg', mg: 'mg',
  ml: 'ml', cl: 'cl', l: 'L', lt: 'L', lts: 'L', litro: 'L', litros: 'L',
  oz: 'oz', lb: 'lb', lbs: 'lb', libra: 'lb', libras: 'lb',
};

/**
 * The size, the way the shop writes one: «37 g (1.2 oz)» is «37 g», «1,5l» is «1.5 L». Null for
 * what is not a measure the shop would print on a shelf («42 paketi», «2 x 330 ml»).
 */
export function cantidadLimpia(cantidad: string | undefined): string | null {
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*([a-zA-Z]+)\b/.exec(cantidad ?? '');
  const unidad = m ? UNIDADES[m[2]!.toLowerCase()] : undefined;
  return m && unidad ? `${m[1]!.replace(',', '.')} ${unidad}` : null;
}

/**
 * The name a new product starts from: the brand when the name does not already say it, then the
 * name, then the size when the name has not got it, as the shop names its own («Gaseosa 1.5
 * litros»). Within the 80 characters a product's name may have.
 */
export function nombreSugerido(d: DatosAbiertos): string {
  let nombre = d.nombre?.trim() ?? '';
  const marca = d.marca?.trim();
  if (marca && !normalizar(nombre).includes(normalizar(marca))) nombre = `${marca} ${nombre}`;
  const cantidad = cantidadLimpia(d.cantidad);
  const numero = cantidad?.split(' ')[0];
  if (cantidad && numero && !normalizar(nombre).replace(',', '.').includes(numero)) nombre = `${nombre} ${cantidad}`;
  return cortarTexto(nombre.replace(/\s+/g, ' ').trim(), 80).trim();
}

/**
 * The shop's category for what the catalogue files under its own: the first of these whose tags
 * it carries, so milk is «Lácteos» before it is a drink and coffee is «Abarrotes». Food the list
 * does not place is «Abarrotes»; toiletries are «Higiene personal»; other goods «Otros».
 */
const CATEGORIAS: readonly [string, readonly string[]][] = [
  ['Lácteos', ['en:dairies', 'en:milks', 'en:cheeses', 'en:yogurts', 'en:butters', 'en:creams', 'en:fermented-milk-products']],
  ['Carnes y embutidos', ['en:meats', 'en:prepared-meats', 'en:sausages', 'en:hams', 'en:poultries']],
  ['Panadería', ['en:breads', 'en:pastries', 'en:viennoiseries']],
  ['Granos básicos', ['en:rices', 'en:beans', 'en:common-beans', 'en:lentils', 'en:dried-legumes']],
  ['Abarrotes', ['en:coffees', 'en:teas', 'en:instant-coffees', 'en:cocoa-and-its-products']],
  [
    'Golosinas',
    ['en:snacks', 'en:sweet-snacks', 'en:salty-snacks', 'en:confectioneries', 'en:candies', 'en:chocolates', 'en:biscuits', 'en:biscuits-and-cakes', 'en:chewing-gum', 'en:crisps'],
  ],
  ['Bebidas', ['en:beverages', 'en:waters', 'en:sodas', 'en:juices', 'en:soft-drinks', 'en:carbonated-drinks', 'en:alcoholic-beverages', 'en:beers', 'en:energy-drinks']],
  ['Frutas y verduras', ['en:fresh-fruits', 'en:fresh-vegetables']],
  ['Limpieza', ['en:cleaning-products', 'en:household-products', 'en:laundry', 'en:detergents', 'en:dishwashing-products', 'en:household-cleaning']],
  ['Higiene personal', ['en:hygiene', 'en:toiletries', 'en:shampoos', 'en:soaps', 'en:toothpastes', 'en:deodorants', 'en:diapers', 'en:toilet-paper']],
];

export function categoriaSugerida(d: DatosAbiertos): string {
  const tags = new Set(d.categorias ?? []);
  for (const [categoria, propias] of CATEGORIAS) if (propias.some((t) => tags.has(t))) return categoria;
  if (d.fuente === 'Open Beauty Facts') return 'Higiene personal';
  if (d.fuente === 'Open Products Facts') return 'Otros';
  return 'Abarrotes';
}

/** Words that say nothing about which product it is. */
const VACIAS = new Set([
  'de', 'del', 'la', 'el', 'los', 'las', 'con', 'sin', 'para', 'por', 'en', 'and', 'the', 'with', 'of',
  'sabor', 'original', 'clasico', 'clasica', 'tradicional', 'natural', 'nuevo', 'nueva', 'pack', 'paquete',
  'bolsa', 'botella', 'lata', 'caja', 'unidad', 'unidades', 'litro', 'litros', 'gramos', 'libra', 'libras',
  ...Object.keys(UNIDADES),
]);
/**
 * What half a shop is called: such words count towards a match, never make one alone. In English
 * too, every product of the starter catalogue having an English name and the catalogues often
 * one: «Whole chicken» is not like an evaporated whole milk (the owner's test shop, 2026-10-02).
 */
const GENERICAS = new Set([
  'galleta', 'galletas', 'leche', 'agua', 'jugo', 'arroz', 'frijol', 'frijoles', 'aceite', 'azucar', 'cafe', 'harina',
  'pan', 'queso', 'jabon', 'detergente', 'refresco', 'gaseosa', 'bebida', 'salsa', 'sopa', 'pasta', 'atun', 'sardina',
  'chocolate', 'dulce', 'dulces', 'caramelo', 'caramelos', 'chicles', 'cereal', 'avena', 'sal', 'crema', 'mantequilla',
  'margarina', 'mayonesa', 'mostaza', 'vinagre', 'papel', 'toallas', 'shampoo', 'desodorante', 'cerveza', 'yogur',
  'yogurt', 'jamon', 'salchicha', 'mani', 'tortilla', 'fideos', 'espagueti', 'cloro', 'lavaplatos', 'panal', 'papa', 'papas',
  'snack', 'snacks', 'gelatina', 'consome', 'ron', 'pollo', 'carne', 'cerdo', 'huevo', 'huevos', 'maiz', 'tomate',
  'entero', 'entera',
  'cookie', 'cookies', 'biscuit', 'biscuits', 'cracker', 'crackers', 'milk', 'water', 'juice', 'rice', 'bean', 'beans',
  'oil', 'sugar', 'coffee', 'flour', 'bread', 'cheese', 'soap', 'detergent', 'soda', 'drink', 'drinks', 'beverage',
  'sauce', 'soup', 'tuna', 'sardine', 'sardines', 'candy', 'candies', 'sweets', 'gum', 'oat', 'oats', 'salt', 'cream',
  'butter', 'margarine', 'mayonnaise', 'mustard', 'vinegar', 'paper', 'towels', 'deodorant', 'beer', 'ham', 'sausage',
  'sausages', 'peanut', 'peanuts', 'tortillas', 'noodles', 'spaghetti', 'bleach', 'diaper', 'diapers', 'potato',
  'potatoes', 'chips', 'gelatin', 'stock', 'rum', 'chicken', 'meat', 'beef', 'pork', 'egg', 'eggs', 'corn', 'tomato',
  'whole',
]);

/**
 * What a catalogue's categories are called on the shop's shelves. The starter catalogue names
 * what it sells, not whose it is («Gaseosa 1.5 litros», «Café instantáneo»), so a Coca-Cola is
 * found among the shop's products by being a soda, and by its size.
 */
const PALABRAS_DE_CATEGORIA: Readonly<Record<string, string>> = {
  'en:sodas': 'gaseosa', 'en:colas': 'gaseosa', 'en:carbonated-drinks': 'gaseosa',
  'en:waters': 'agua', 'en:juices': 'jugo', 'en:fruit-juices': 'jugo', 'en:nectars': 'jugo',
  'en:energy-drinks': 'bebida energetica', 'en:beers': 'cerveza', 'en:rums': 'ron',
  'en:instant-coffees': 'cafe instantaneo', 'en:ground-coffees': 'cafe molido',
  'en:biscuits': 'galletas', 'en:crackers': 'galletas', 'en:cookies': 'galletas',
  'en:candies': 'caramelos', 'en:chewing-gum': 'chicles', 'en:chocolates': 'chocolate',
  // Not chips: their word here would be «papas», and that is the potatoes.
  'en:salted-peanuts': 'mani salado',
  'en:soups': 'sopa', 'en:instant-soups': 'sopa', 'en:bouillon-cubes': 'consome cubito',
  'en:tomato-sauces': 'salsa tomate', 'en:ketchup': 'salsa tomate', 'en:mayonnaises': 'mayonesa',
  'en:mustards': 'mostaza', 'en:vinegars': 'vinagre', 'en:canned-tunas': 'atun', 'en:sardines': 'sardina',
  'en:spaghetti': 'espagueti', 'en:pastas': 'fideos', 'en:noodles': 'fideos',
  'en:milk-powders': 'leche polvo', 'en:breakfast-cereals': 'cereal', 'en:rolled-oats': 'avena',
  'en:rices': 'arroz', 'en:sugars': 'azucar', 'en:salts': 'sal', 'en:flours': 'harina',
  'en:vegetable-oils': 'aceite vegetal', 'en:butters': 'mantequilla', 'en:yogurts': 'yogur',
  'en:cheeses': 'queso', 'en:hams': 'jamon', 'en:sausages': 'salchicha', 'en:breads': 'pan',
  'en:sliced-breads': 'pan molde', 'en:tortillas': 'tortilla', 'en:toothpastes': 'pasta dental',
  'en:shampoos': 'shampoo', 'en:deodorants': 'desodorante', 'en:diapers': 'panal', 'en:toilet-paper': 'papel higienico',
  'en:soaps': 'jabon', 'en:laundry-detergents': 'detergente', 'en:dishwashing-liquids': 'lavaplatos',
};

/** A text's words that could tell a product apart: accents and case aside, «37g» as 37 and g. */
function palabras(texto: string): string[] {
  return normalizar(texto)
    .replace(/(\d)([a-z])/g, '$1 $2')
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3 && !/^\d/.test(w) && !VACIAS.has(w));
}

/** A size, in grams or millilitres where it can be, so «1.5 litros» and «1500 ml» are one. */
interface Medida {
  valor: number;
  unidad: string;
}
const A_BASE: Readonly<Record<string, [number, string]>> = {
  g: [1, 'g'], kg: [1_000, 'g'], mg: [0.001, 'g'], ml: [1, 'ml'], cl: [10, 'ml'], L: [1_000, 'ml'], oz: [1, 'oz'], lb: [1, 'lb'],
};

/** Every size a text states: «Gaseosa 1.5 litros» is 1500 ml; «37 g (1.2 oz)» is 37 g and 1.2 oz. */
export function medidas(texto: string): Medida[] {
  const normal = normalizar(texto).replace(/(\d),(\d)/g, '$1.$2');
  return [...normal.matchAll(/(\d+(?:\.\d+)?)\s*([a-z]+)/g)].flatMap((m) => {
    const unidad = UNIDADES[m[2]!];
    const base = unidad ? A_BASE[unidad] : undefined;
    return base ? [{ valor: Number(m[1]) * base[0], unidad: base[1] }] : [];
  });
}

const mismaMedida = (a: Medida, b: Medida) =>
  a.unidad === b.unidad && Math.abs(a.valor - b.valor) <= Math.max(a.valor, b.valor) * 0.02;

/** The same word, or one a plural or a cut of the other: «galleta» and «galletas». */
const parecida = (a: string, b: string) =>
  a === b || (Math.min(a.length, b.length) >= 4 && (a.startsWith(b) || b.startsWith(a)));

/**
 * The shop's own products that look like what the catalogue describes, best first, at most
 * three: the can just scanned is often one the shop already sells under a name of its own, with
 * no code yet (the starter catalogue has none).
 *
 * A word that tells products apart, a brand or a name, is a match. Words half the shop shares
 * («gaseosa», «galletas», what the catalogue's categories are called here) are a match two at a
 * time, or one with the same size, or one that is the product's whole name («Galletas»). A size
 * on both that is not the same rules a product out: the 37 g can's code is not the 124 g one's.
 * Products with no code yet come first among equals, being the ones still waiting for theirs.
 */
export function parecidos(productos: readonly Producto[], d: DatosAbiertos, maximo = 3): Producto[] {
  const deCategoria = (d.categorias ?? []).flatMap((c) => PALABRAS_DE_CATEGORIA[c] ?? []);
  const buscadas = [...new Set(palabras([d.marca, d.nombre, d.nombreEn, ...deCategoria].filter(Boolean).join(' ')))];
  const tamanos = medidas(d.cantidad ?? '');
  if (buscadas.length === 0) return [];

  return productos
    .filter((p) => p.activo)
    .flatMap((p) => {
      const nombres = nombresDe(p);
      const suyos = medidas(nombres.join(' '));
      const mismoTamano = suyos.some((m) => tamanos.some((t) => mismaMedida(m, t)));
      if (tamanos.length > 0 && suyos.length > 0 && !mismoTamano) return [];
      // Each name on its own, in its own language: «Café molido» and «Ground coffee» are one word
      // of the coffee's twice, not two; and «Galletas» is the whole of one, whatever its English says.
      const puntos = Math.max(
        ...nombres.map((n) => {
          const suyas = palabras(n);
          const halladas = buscadas.filter((w) => suyas.some((s) => parecida(w, s)));
          const propias = halladas.filter((w) => !GENERICAS.has(w)).length;
          const genericas = halladas.length - propias;
          const entero = suyas.length > 0 && suyas.every((s) => buscadas.some((w) => parecida(w, s)));
          const vale = propias > 0 || genericas >= 2 || (genericas === 1 && (mismoTamano || entero));
          return vale ? propias * 3 + genericas : -1;
        }),
      );
      return puntos >= 0 ? [{ p, puntos: puntos + (mismoTamano ? 2 : 0) }] : [];
    })
    .sort(
      (a, b) =>
        b.puntos - a.puntos ||
        Number(a.p.codigosBarras.length > 0) - Number(b.p.codigosBarras.length > 0) ||
        a.p.nombre.localeCompare(b.p.nombre, 'es'),
    )
    .slice(0, maximo)
    .map((x) => x.p);
}
